import { currencyFormatter } from '@/helpers/currencyFormatter';
import { useGetAccountQuery } from '@/services/accounts';
import { Alert, Box, Button, Column, Table } from '@palmetto/palmetto-components';
import { useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { BatteryPricingMethodOption, ProgramType } from 'types';

import { useAuth } from '../auth/authProvider';
import { isAdminUser } from '../auth/RequireAdminUser';
import FormSection from '../Forms/FormSection';

import { CreateQuoteFormikContext } from './CreateQuote';
import { useGetAccountPricing } from './hooks/useGetAccountPricing';
import './quoteTable.css';

type QuoteTableData = {
  productId: string;
  kwhRate: string;
  firstMonthlyPayment: string;
  solarOnlyEPCPayment: string;
  totalEPCPayment?: string;
  batteryMonthlyPayment?: string;
  totalMonthlyPayment?: string;
};

const columnConfig = [
  {
    heading: 'Solar-only Rate ($/kWh)',
    headerClassName: 'wrap',
    dataKey: 'kwhRate',
  },
  {
    heading: 'Estimated solar-only year-1 Mo. Payment',
    dataKey: 'firstMonthlyPayment',
    headerClassName: 'wrap',
  },
  {
    heading: 'Solar-only EPC Rate ($/W)',
    dataKey: 'ppw',
    headerClassName: 'wrap',
  },
  {
    heading: 'Solar-only EPC Total Payment',
    dataKey: 'solarOnlyEPCPayment',
    headerClassName: 'wrap',
  },
];

const columnConfigWithDC = [
  ...columnConfig,
  {
    heading: 'Solar-only EPC Rate ($/W) without DC',
    dataKey: 'ppwWithoutDC',
    headerClassName: 'wrap',
  },
  {
    heading: 'Solar-only EPC Total Payment without DC',
    headerClassName: 'wrap',
    dataKey: 'solarOnlyEPCPaymentWithoutDC',
  },
];

const columnConfigWithDCAndBattery = [
  ...columnConfig,
  {
    heading: 'Solar-only EPC Rate ($/W) without DC',
    dataKey: 'ppwWithoutDC',
    headerClassName: 'wrap',
  },
  {
    heading: 'Solar-only EPC Total Payment without DC',
    headerClassName: 'wrap',
    dataKey: 'solarOnlyEPCPaymentWithoutDC',
  },
  {
    heading: 'Estimated Battery Year 1 Monthly Payment',
    headerClassName: 'wrap',
    dataKey: 'batteryPayment',
  },
  {
    heading: 'Total Year 1 Monthly Payment',
    headerClassName: 'wrap',
    dataKey: 'totalMonthlyPayment',
  },
  {
    heading: 'Total EPC Payment',
    headerClassName: 'wrap',
    dataKey: 'totalEPCPayment',
  },
  {
    heading: 'Total EPC Payment without DC',
    headerClassName: 'wrap',
    dataKey: 'totalEPCPaymentWithoutDC',
  },
  {
    heading: 'Effective EPC Rate ($/W)',
    dataKey: 'effectivePPWRate',
    headerClassName: 'wrap',
  },
];

const columnConfigWithBattery = [
  ...columnConfig,
  {
    heading: 'Estimated Battery Year 1 Monthly Payment',
    headerClassName: 'wrap',
    dataKey: 'batteryPayment',
  },
  {
    heading: 'Total Year 1 Monthly Payment',
    headerClassName: 'wrap',
    dataKey: 'totalMonthlyPayment',
  },
  {
    heading: 'Total EPC Payment',
    headerClassName: 'wrap',
    dataKey: 'totalEPCPayment',
  },
  {
    heading: 'Effective EPC Rate ($/W)',
    dataKey: 'effectivePPWRate',
    headerClassName: 'wrap',
  },
];

export const PricingTable = ({ headerContent }: { headerContent?: React.ReactNode } = {}) => {
  const { monthlyPaymentCap = false } = useFlags();
  const { claims } = useAuth();
  const { id } = useParams<{ id: any }>();
  const { data: accountData } = useGetAccountQuery(id);
  const { values, setFieldValue } = useFormikContext<CreateQuoteFormikContext>();
  const { products, showDCComparison, escalationRates, isDCPricingDifferent, isLoading, isFetching } =
    useGetAccountPricing(values);

  useEffect(() => {
    if (
      monthlyPaymentCap &&
      !products?.length &&
      !isLoading &&
      !isFetching &&
      accountData?.programType !== ProgramType.doePr // DoePR accounts will return no pricing at first and requires a battery selection
    ) {
      setFieldValue('noPricingOptionsAvailable', true);
    } else {
      setFieldValue('noPricingOptionsAvailable', false);
    }

    if (
      monthlyPaymentCap &&
      products?.length &&
      products[0].resultsLimitedByMonthlyPaymentCap &&
      !values.pricingOptionsLimitedByMonthlyPaymentCap
    ) {
      setFieldValue('pricingOptionsLimitedByMonthlyPaymentCap', true);
    }
  }, [
    monthlyPaymentCap,
    products,
    values.pricingOptionsLimitedByMonthlyPaymentCap,
    isLoading,
    isFetching,
    setFieldValue,
    accountData?.programType,
  ]);

  const escalationRateAndProductData = useMemo(() => {
    const escalationRateAndProduct: Array<{ escalationRate: number; tableData: QuoteTableData[] }> = [];
    escalationRates?.forEach((rate) => {
      const productsByEscalationRate = products?.filter((product) => product.escalationRate === rate);
      const kwhRateAndEpcAndFirstMonthlyPayment =
        productsByEscalationRate?.map((product) => ({
          productId: product.productId,
          kwhRate: `$${product.kwhRate}`,
          firstMonthlyPayment: currencyFormatter.format(product.monthlyPayments[0].monthlyPayment),
          effectivePPWRate: currencyFormatter.format(product.effectivePpwRate),
          ppw: currencyFormatter.format(product.ppwRate),
          ppwWithoutDC: currencyFormatter.format(product.ppwWithoutDC),
          solarOnlyEPCPayment: currencyFormatter.format(product.solarOnlyEPCPayment),
          solarOnlyEPCPaymentWithoutDC: currencyFormatter.format(product.solarOnlyEPCPaymentWithoutDC),
          totalEPCPayment: product.totalEPCPayment ? currencyFormatter.format(product.totalEPCPayment) : undefined,
          totalEPCPaymentWithoutDC: product.totalEPCPaymentWithoutDC
            ? currencyFormatter.format(product.totalEPCPaymentWithoutDC)
            : undefined,
          batteryPayment:
            product.monthlyPayments[0]?.batteryPayment !== undefined
              ? currencyFormatter.format(product.monthlyPayments[0].batteryPayment)
              : undefined,
          totalMonthlyPayment:
            product.monthlyPayments[0]?.totalMonthlyPayment !== undefined
              ? currencyFormatter.format(product.monthlyPayments[0].totalMonthlyPayment)
              : undefined,
        })) || [];
      escalationRateAndProduct.push({
        escalationRate: rate,
        tableData: kwhRateAndEpcAndFirstMonthlyPayment,
      });
    });
    const selectedEscalationRateAndProduct =
      values.selectedEscalationRate !== undefined
        ? escalationRateAndProduct.find((products) => products.escalationRate === values.selectedEscalationRate)
        : escalationRateAndProduct[0];
    return selectedEscalationRateAndProduct;
  }, [products, accountData, escalationRates, values.selectedEscalationRate]);

  const selectProductColumn = {
    heading: '',
    dataKey: 'productId',
    sticky: 'right',
    render: (_cell: any, row: QuoteTableData) => {
      const isSelected = values.productId === row.productId;
      return (
        <>
          {/* Marker consumed by the :has() rule in quoteTable.css to highlight the selected row. */}
          {isSelected && <span data-selected-pricing-row style={{ display: 'none' }} />}
          <Button
            as="button"
            onClick={() => setFieldValue('productId', row.productId)}
            variant={isSelected ? 'primary' : 'secondary'}
          >
            Select
          </Button>
        </>
      );
    },
  };

  // don't show the DC columns to non admins when the dc pricing is the same
  const shouldShowDcColumns = isDCPricingDifferent || isAdminUser(claims);

  const getColumnConfig = (): Column[] =>
    values?.battery?.pricingMethod === BatteryPricingMethodOption.rawCost
      ? showDCComparison && shouldShowDcColumns
        ? ([...columnConfigWithDCAndBattery, selectProductColumn] as Column[])
        : ([...columnConfigWithBattery, selectProductColumn] as Column[])
      : showDCComparison && shouldShowDcColumns
        ? ([...columnConfigWithDC, selectProductColumn] as Column[])
        : ([...columnConfig, selectProductColumn] as Column[]);

  return (
    <FormSection title="Pricing Options">
      {headerContent}
      {values.noPricingOptionsAvailable && (
        <Alert
          variant="warning"
          message={`No pricing options are available for this account.${
            accountData?.monthlyPaymentCap &&
            accountData.monthlyPaymentCap.orgMaxZeroEscalationRate !== undefined &&
            accountData.monthlyPaymentCap.zeroEscalationRate !==
              accountData.monthlyPaymentCap.orgMaxZeroEscalationRate
              ? ` This may be because all pricing options exceed this customer's maximum monthly payment cap.`
              : ``
          }`}
        ></Alert>
      )}
      {values.pricingOptionsLimitedByMonthlyPaymentCap && (
        <Alert
          variant="warning"
          message="Pricing options are currently limited based on the credit applicant or applicants (if there are co-applicants) on this account. Additional options may be available by adding a new co-applicant."
        ></Alert>
      )}
      <Box display="block">
        <Table
          className="pricing-options-table"
          rowKey="productId"
          columns={getColumnConfig()}
          rows={escalationRateAndProductData?.tableData || []}
          isLoading={isLoading || isFetching}
          isScrollable={{
            x: true,
          }}
        />
      </Box>
    </FormSection>
  );
};
